import { BaseEffect } from '../BaseEffect.js';

/**
 * Chromatic aberration effect implementation
 * Creates dramatic RGB color splitting with glow and distortion
 */
export class ChromaticEffect extends BaseEffect {
    constructor(shaderManager) {
        const defaultParams = {
            rgbShift: 0.03,       // Base RGB separation amount
            distortion: 0.2,      // Lens distortion amount
            rgbGlow: 0.5,        // RGB glow intensity
            vignetteAmount: 0.3,  // Vignette darkness
            scanlines: 0.1,      // Scanline effect intensity
            pulseSpeed: 1.0,     // Speed of pulsing animation
            pulseAmount: 0.2,    // Amount of pulsing
            animate: true        // Whether to animate the effect
        };

        super('chromatic-aberration', shaderManager, defaultParams);
    }

    /**
     * Initialize the effect
     */
    async init() {
        const vertexSource = `
            attribute vec2 a_position;
            attribute vec2 a_texCoord;
            varying vec2 v_texcoord;
            
            void main() {
                gl_Position = vec4(a_position, 0, 1);
                v_texcoord = a_texCoord;
            }
        `;

        const fragmentSource = `
            precision highp float;
            
            uniform sampler2D u_image;
            uniform float u_time;
            uniform vec2 u_resolution;
            uniform float u_rgbShift;
            uniform float u_distortion;
            uniform float u_rgbGlow;
            uniform float u_vignetteAmount;
            uniform float u_scanlines;
            uniform float u_pulseSpeed;
            uniform float u_pulseAmount;
            
            varying vec2 v_texcoord;

            // Improved chromatic sampling with stronger glow
            vec4 sampleWithGlow(vec2 uv, vec2 direction, float channel) {
                vec4 sum = vec4(0.0);
                float totalWeight = 0.0;
                
                // Sample multiple times for glow with larger offsets
                for(int i = 0; i < 6; i++) {
                    float weight = pow(0.7, float(i));
                    float offset = float(i) * 0.005 * u_rgbGlow;
                    vec2 sampleUV = uv + direction * offset;
                    vec4 texColor = texture2D(u_image, sampleUV);
                    
                    // Improved RGB separation
                    if (channel == 0.0) { // Red
                        sum += vec4(texColor.r, 0.0, 0.0, texColor.a) * weight;
                    } else if (channel == 1.0) { // Green
                        sum += vec4(0.0, texColor.g, 0.0, texColor.a) * weight;
                    } else { // Blue
                        sum += vec4(0.0, 0.0, texColor.b, texColor.a) * weight;
                    }
                    
                    totalWeight += weight;
                }
                
                return sum / totalWeight;
            }

            // Enhanced barrel distortion
            vec2 distort(vec2 uv) {
                vec2 center = uv - 0.5;
                float dist = length(center);
                float distortionFactor = 1.0 + (u_distortion * 2.0) * dist * dist;
                return center * distortionFactor + 0.5;
            }

            void main() {
                vec2 uv = v_texcoord;
                float aspect = u_resolution.x / u_resolution.y;
                
                // Apply enhanced distortion
                vec2 distortedUV = distort(uv);
                
                // Calculate direction with stronger edge effect
                vec2 center = distortedUV - 0.5;
                center.x *= aspect;
                float dist = length(center);
                vec2 direction = normalize(center);
                
                // Enhanced pulsing animation
                float time = u_time * u_pulseSpeed;
                float pulse = 1.0 + sin(time) * u_pulseAmount + cos(time * 0.7) * u_pulseAmount * 0.5;
                float shift = u_rgbShift * pulse * (1.0 + dist * 2.0);
                
                // Sample with enhanced RGB separation
                vec4 redChannel = sampleWithGlow(distortedUV + direction * shift, direction, 0.0);
                vec4 greenChannel = sampleWithGlow(distortedUV, direction, 1.0);
                vec4 blueChannel = sampleWithGlow(distortedUV - direction * shift * 1.2, direction, 2.0);
                
                // Combine channels with enhanced contrast
                vec4 color = vec4(
                    redChannel.r,
                    greenChannel.g,
                    blueChannel.b,
                    (redChannel.a + greenChannel.a + blueChannel.a) / 3.0
                );
                
                // Enhanced vignette
                float vignette = 1.0 - smoothstep(0.0, 1.0, dist * u_vignetteAmount * 3.0);
                color.rgb *= vignette;
                
                // Enhanced scanlines with motion
                float scanPos = uv.y * u_resolution.y * 1.0 - u_time * 8.0;
                float scanline = sin(scanPos) * 0.5 + 0.5;
                color.rgb *= 1.0 - (scanline * u_scanlines);
                
                // Color enhancement
                color.rgb *= 1.2;
                color.rgb = pow(color.rgb, vec3(0.85));
                
                gl_FragColor = color;
            }
        `;

        await super.init(vertexSource, fragmentSource);
    }

    /**
     * Set up effect-specific uniforms
     */
    setupUniforms() {
        super.setupUniforms();
        
        this.addUniform('u_rgbShift', { type: '1f' });
        this.addUniform('u_distortion', { type: '1f' });
        this.addUniform('u_rgbGlow', { type: '1f' });
        this.addUniform('u_vignetteAmount', { type: '1f' });
        this.addUniform('u_scanlines', { type: '1f' });
        this.addUniform('u_pulseSpeed', { type: '1f' });
        this.addUniform('u_pulseAmount', { type: '1f' });
    }

    /**
     * Update effect-specific uniforms
     * @param {Object} uniforms - Uniform values to update
     */
    updateUniforms(uniforms) {
        super.updateUniforms(uniforms);
        
        Object.assign(uniforms, {
            u_rgbShift: { value: this.params.rgbShift, type: '1f' },
            u_distortion: { value: this.params.distortion, type: '1f' },
            u_rgbGlow: { value: this.params.rgbGlow, type: '1f' },
            u_vignetteAmount: { value: this.params.vignetteAmount, type: '1f' },
            u_scanlines: { value: this.params.scanlines, type: '1f' },
            u_pulseSpeed: { value: this.params.pulseSpeed, type: '1f' },
            u_pulseAmount: { value: this.params.pulseAmount, type: '1f' }
        });

        if (!this.params.animate) {
            uniforms.u_time.value = 0;
        }
    }

    /**
     * Get UI configuration for parameters
     * @returns {Object} UI configuration
     */
    getUIConfig() {
        return {
            rgbShift: {
                min: 0,
                max: 0.2,
                step: 0.001,
                label: 'RGB Split Amount'
            },
            distortion: {
                min: -1,
                max: 1,
                step: 0.01,
                label: 'Lens Distortion'
            },
            rgbGlow: {
                min: 0,
                max: 2,
                step: 0.1,
                label: 'RGB Glow'
            },
            vignetteAmount: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Vignette'
            },
            scanlines: {
                min: 0,
                max: 0.5,
                step: 0.01,
                label: 'Scanlines'
            },
            pulseSpeed: {
                min: 0,
                max: 5,
                step: 0.1,
                label: 'Pulse Speed'
            },
            pulseAmount: {
                min: 0,
                max: 0.5,
                step: 0.01,
                label: 'Pulse Amount'
            },
            animate: {
                label: 'Animate'
            }
        };
    }
} 